
import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';

interface AffiliateData {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  status: string;
  commission_rate: number;
}

interface MobileAffiliateCardProps {
  affiliate: AffiliateData;
  onStatusChange: (affiliateId: string, newStatus: 'active' | 'suspended' | 'pending') => void;
  onCommissionChange: (affiliateId: string, newRate: number) => void;
}

export const MobileAffiliateCard = ({ 
  affiliate, 
  onStatusChange, 
  onCommissionChange 
}: MobileAffiliateCardProps) => {
  const getBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base truncate">{affiliate.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={getBadgeColor(affiliate.status)}>
                {affiliate.status}
              </Badge>
              <span className="text-sm font-medium text-muted-foreground">
                {(affiliate.commission_rate * 100).toFixed(0)}%
              </span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onStatusChange(affiliate.id, 'active')}>
                Set as Active
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onStatusChange(affiliate.id, 'pending')}>
                Set as Pending
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onStatusChange(affiliate.id, 'suspended')}>
                Suspend Affiliate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCommissionChange(affiliate.id, 0.05)}>
                Set Commission: 5%
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCommissionChange(affiliate.id, 0.10)}>
                Set Commission: 10%
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCommissionChange(affiliate.id, 0.15)}>
                Set Commission: 15%
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-2">
        <div className="text-sm">
          <span className="text-muted-foreground">Email:</span>
          <p className="font-medium truncate">{affiliate.email}</p>
        </div>
        <div className="text-sm">
          <span className="text-muted-foreground">Referral Code:</span>
          <code className="text-xs bg-muted px-1 py-0.5 rounded block mt-1">
            {affiliate.referral_code}
          </code>
        </div>
      </CardContent>
    </Card>
  );
};
